import { lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';

import { CONFIG } from 'src/config-global';
import { DashboardLayout } from 'src/layouts/dashboard';

import { SplashScreen } from 'src/components/loading-screen';

import { OpenPlayProvider } from 'src/sections/open-play/context/open-play-context';

import { AuthGuard, RBACGuard } from 'src/auth/guard';

// ---------------------------------------------------------------------------
// Lazy-loaded page modules. Folder names with [id] are kept as-is on disk;
// in route paths we use react-router's :id syntax.
// ---------------------------------------------------------------------------
const DashboardHome = lazy(() => import('src/pages/dashboard/page'));
const Analytics = lazy(() => import('src/pages/dashboard/analytics/page'));
const Blank = lazy(() => import('src/pages/dashboard/blank/page'));
const Chat = lazy(() => import('src/pages/dashboard/chat/page'));
const Feedback = lazy(() => import('src/pages/dashboard/feedback/page'));
const Params = lazy(() => import('src/pages/dashboard/params/page'));
const Permission = lazy(() => import('src/pages/dashboard/permission/page'));
const Permissions = lazy(() => import('src/pages/dashboard/permissions/page'));

// admin/*
const FacilityOwnersList = lazy(() => import('src/pages/dashboard/admin/facility-owners/page'));
const FacilityOwnersNew = lazy(() => import('src/pages/dashboard/admin/facility-owners/new/page'));
const FacilityOwnersDetails = lazy(() => import('src/pages/dashboard/admin/facility-owners/[id]/page'));
const FacilityOwnersEdit = lazy(() => import('src/pages/dashboard/admin/facility-owners/[id]/edit/page'));
const ManagersList = lazy(() => import('src/pages/dashboard/admin/managers/page'));
const ManagersNew = lazy(() => import('src/pages/dashboard/admin/managers/new/page'));
const ManagersDetails = lazy(() => import('src/pages/dashboard/admin/managers/[id]/page'));
const ManagersEdit = lazy(() => import('src/pages/dashboard/admin/managers/[id]/edit/page'));

// ads/*
const AdsList = lazy(() => import('src/pages/dashboard/ads/page'));
const AdsNew = lazy(() => import('src/pages/dashboard/ads/new/page'));
const AdsDetails = lazy(() => import('src/pages/dashboard/ads/[id]/page'));
const AdsEdit = lazy(() => import('src/pages/dashboard/ads/[id]/edit/page'));
const AdsPlaylistsList = lazy(() => import('src/pages/dashboard/ads/playlists/page'));
const AdsPlaylistsNew = lazy(() => import('src/pages/dashboard/ads/playlists/new/page'));
const AdsPlaylistsDetails = lazy(() => import('src/pages/dashboard/ads/playlists/[id]/page'));
const AdsPlaylistsEdit = lazy(() => import('src/pages/dashboard/ads/playlists/[id]/edit/page'));
const AdsSchedulesList = lazy(() => import('src/pages/dashboard/ads/schedules/page'));
const AdsSchedulesNew = lazy(() => import('src/pages/dashboard/ads/schedules/new/page'));
const AdsSchedulesEdit = lazy(() => import('src/pages/dashboard/ads/schedules/[id]/edit/page'));

// app-versions/*
const AppVersionsList = lazy(() => import('src/pages/dashboard/app-versions/page'));
const AppVersionsNew = lazy(() => import('src/pages/dashboard/app-versions/new/page'));
const AppVersionsDetails = lazy(() => import('src/pages/dashboard/app-versions/[id]/page'));
const AppVersionsEdit = lazy(() => import('src/pages/dashboard/app-versions/[id]/edit/page'));

// audit-logs/*
const AuditLogsList = lazy(() => import('src/pages/dashboard/audit-logs/page'));
const AuditLogsDetails = lazy(() => import('src/pages/dashboard/audit-logs/[id]/page'));

// banners/*
const BannersList = lazy(() => import('src/pages/dashboard/banners/page'));
const BannersNew = lazy(() => import('src/pages/dashboard/banners/new/page'));
const BannersDetails = lazy(() => import('src/pages/dashboard/banners/[id]/page'));
const BannersEdit = lazy(() => import('src/pages/dashboard/banners/[id]/edit/page'));

// bookings/*
const BookingsList = lazy(() => import('src/pages/dashboard/bookings/page'));
const BookingsDetails = lazy(() => import('src/pages/dashboard/bookings/[id]/page'));
const BookingsEdit = lazy(() => import('src/pages/dashboard/bookings/[id]/edit/page'));

// bugs/*
const BugsList = lazy(() => import('src/pages/dashboard/bugs/page'));
const BugsNew = lazy(() => import('src/pages/dashboard/bugs/new/page'));
const BugsDetails = lazy(() => import('src/pages/dashboard/bugs/[id]/page'));
const BugsEdit = lazy(() => import('src/pages/dashboard/bugs/[id]/edit/page'));

// clubs/*
const ClubsList = lazy(() => import('src/pages/dashboard/clubs/page'));
const ClubsNew = lazy(() => import('src/pages/dashboard/clubs/new/page'));
const ClubsDetails = lazy(() => import('src/pages/dashboard/clubs/[id]/page'));
const ClubsEdit = lazy(() => import('src/pages/dashboard/clubs/[id]/edit/page'));

// facilities/*
const FacilitiesList = lazy(() => import('src/pages/dashboard/facilities/page'));
const FacilitiesNew = lazy(() => import('src/pages/dashboard/facilities/new/page'));
const FacilitiesDetails = lazy(() => import('src/pages/dashboard/facilities/[id]/page'));
const FacilitiesEdit = lazy(() => import('src/pages/dashboard/facilities/[id]/edit/page'));

// leaderboard/*
const LeaderboardList = lazy(() => import('src/pages/dashboard/leaderboard/page'));
const LeaderboardDetails = lazy(() => import('src/pages/dashboard/leaderboard/[id]/page'));

// matches/*
const MatchesList = lazy(() => import('src/pages/dashboard/matches/page'));
const MatchesDetails = lazy(() => import('src/pages/dashboard/matches/[id]/page'));
const MatchesEdit = lazy(() => import('src/pages/dashboard/matches/[id]/edit/page'));
const MatchesLive = lazy(() => import('src/pages/dashboard/matches/[id]/live/page'));
const MatchesPlayers = lazy(() => import('src/pages/dashboard/matches/[id]/players/page'));

// monitors/*
const MonitorsList = lazy(() => import('src/pages/dashboard/monitors/page'));

// open-play/*
const OpenPlayHome = lazy(() => import('src/pages/dashboard/open-play/page'));
const OpenPlayBoard = lazy(() => import('src/pages/dashboard/open-play/board/page'));
const OpenPlayQueue = lazy(() => import('src/pages/dashboard/open-play/queue/page'));
const OpenPlayWaiting = lazy(() => import('src/pages/dashboard/open-play/waiting/page'));

// payments/*
const PaymentsList = lazy(() => import('src/pages/dashboard/payments/page'));
const PaymentsDetails = lazy(() => import('src/pages/dashboard/payments/[id]/page'));
const PaymentsRevenueAnalytics = lazy(() => import('src/pages/dashboard/payments/reports/page'));

// wallet/*
const WalletTransactions = lazy(() => import('src/pages/dashboard/wallet/page'));

// vouchers/*
const VoucherCampaignsList = lazy(() => import('src/pages/dashboard/vouchers/page'));
const VoucherCampaignNew = lazy(() => import('src/pages/dashboard/vouchers/new/page'));
const VoucherCampaignDetails = lazy(() => import('src/pages/dashboard/vouchers/[id]/page'));
const VoucherCampaignEdit = lazy(() => import('src/pages/dashboard/vouchers/[id]/edit/page'));
const VoucherRedeem = lazy(() => import('src/pages/dashboard/vouchers/redeem/page'));
const VoucherRedemptions = lazy(() => import('src/pages/dashboard/vouchers/redemptions/page'));

// reports/*
const ReportsClubPerformance = lazy(() => import('src/pages/dashboard/reports/club-performance/page'));
const ReportsRevenueAnalytics = lazy(() => import('src/pages/dashboard/reports/revenue-analytics/page'));
const ReportsSystemUsage = lazy(() => import('src/pages/dashboard/reports/system-usage/page'));
const ReportsUserActivity = lazy(() => import('src/pages/dashboard/reports/user-activity/page'));

// sessions/*
const SessionsList = lazy(() => import('src/pages/dashboard/sessions/page'));
const SessionsNew = lazy(() => import('src/pages/dashboard/sessions/new/page'));
const SessionsDetails = lazy(() => import('src/pages/dashboard/sessions/[id]/page'));
const SessionsEdit = lazy(() => import('src/pages/dashboard/sessions/[id]/edit/page'));
const SessionsCourtLineup = lazy(() => import('src/pages/dashboard/sessions/[id]/court-lineup/page'));
const SessionsRecurringList = lazy(() => import('src/pages/dashboard/sessions/recurring/page'));
const SessionsRecurringDetails = lazy(() => import('src/pages/dashboard/sessions/recurring/[id]/page'));
const SessionsRecurringEdit = lazy(() => import('src/pages/dashboard/sessions/recurring/[id]/edit/page'));

// settings/*
const SettingsHome = lazy(() => import('src/pages/dashboard/settings/page'));
const SettingsHealth = lazy(() => import('src/pages/dashboard/settings/health/page'));
const SettingsMaintenance = lazy(() => import('src/pages/dashboard/settings/maintenance/page'));

// support/*
const SupportList = lazy(() => import('src/pages/dashboard/support/page'));
const SupportNew = lazy(() => import('src/pages/dashboard/support/new/page'));
const SupportDetails = lazy(() => import('src/pages/dashboard/support/[id]/page'));
const SupportEdit = lazy(() => import('src/pages/dashboard/support/[id]/edit/page'));

// account/*
const AccountProfile = lazy(() => import('src/pages/dashboard/account/profile/page'));
const AccountSettings = lazy(() => import('src/pages/dashboard/account/settings/page'));

// users/*
const UsersList = lazy(() => import('src/pages/dashboard/users/page'));
const UsersListAlt = lazy(() => import('src/pages/dashboard/users/list/page'));
const UsersProfile = lazy(() => import('src/pages/dashboard/users/profile/page'));
const UsersDetails = lazy(() => import('src/pages/dashboard/users/[id]/page'));

// ---------------------------------------------------------------------------

const dashboardLayout = (
  <DashboardLayout>
    <Suspense fallback={<SplashScreen />}>
      <Outlet />
    </Suspense>
  </DashboardLayout>
);

const guardedLayout = CONFIG.auth.skip ? (
  dashboardLayout
) : (
  <AuthGuard>
    <RBACGuard>{dashboardLayout}</RBACGuard>
  </AuthGuard>
);

const openPlayWrapper = (
  <OpenPlayProvider>
    <Outlet />
  </OpenPlayProvider>
);

export const dashboardRoutes = [
  {
    path: 'dashboard',
    element: guardedLayout,
    children: [
      { index: true, element: <DashboardHome /> },
      { path: 'analytics', element: <Analytics /> },
      { path: 'blank', element: <Blank /> },
      { path: 'chat', element: <Chat /> },
      { path: 'feedback', element: <Feedback /> },
      { path: 'params', element: <Params /> },
      { path: 'permission', element: <Permission /> },
      { path: 'permissions', element: <Permissions /> },

      {
        path: 'admin',
        children: [
          {
            path: 'facility-owners',
            children: [
              { index: true, element: <FacilityOwnersList /> },
              { path: 'new', element: <FacilityOwnersNew /> },
              { path: ':id', element: <FacilityOwnersDetails /> },
              { path: ':id/edit', element: <FacilityOwnersEdit /> },
            ],
          },
          {
            path: 'managers',
            children: [
              { index: true, element: <ManagersList /> },
              { path: 'new', element: <ManagersNew /> },
              { path: ':id', element: <ManagersDetails /> },
              { path: ':id/edit', element: <ManagersEdit /> },
            ],
          },
        ],
      },

      {
        path: 'ads',
        children: [
          { index: true, element: <AdsList /> },
          { path: 'new', element: <AdsNew /> },
          { path: ':id', element: <AdsDetails /> },
          { path: ':id/edit', element: <AdsEdit /> },
          {
            path: 'playlists',
            children: [
              { index: true, element: <AdsPlaylistsList /> },
              { path: 'new', element: <AdsPlaylistsNew /> },
              { path: ':id', element: <AdsPlaylistsDetails /> },
              { path: ':id/edit', element: <AdsPlaylistsEdit /> },
            ],
          },
          {
            path: 'schedules',
            children: [
              { index: true, element: <AdsSchedulesList /> },
              { path: 'new', element: <AdsSchedulesNew /> },
              { path: ':id/edit', element: <AdsSchedulesEdit /> },
            ],
          },
        ],
      },

      {
        path: 'app-versions',
        children: [
          { index: true, element: <AppVersionsList /> },
          { path: 'new', element: <AppVersionsNew /> },
          { path: ':id', element: <AppVersionsDetails /> },
          { path: ':id/edit', element: <AppVersionsEdit /> },
        ],
      },

      {
        path: 'audit-logs',
        children: [
          { index: true, element: <AuditLogsList /> },
          { path: ':id', element: <AuditLogsDetails /> },
        ],
      },

      {
        path: 'banners',
        children: [
          { index: true, element: <BannersList /> },
          { path: 'new', element: <BannersNew /> },
          { path: ':id', element: <BannersDetails /> },
          { path: ':id/edit', element: <BannersEdit /> },
        ],
      },

      {
        path: 'bookings',
        children: [
          { index: true, element: <BookingsList /> },
          { path: ':id', element: <BookingsDetails /> },
          { path: ':id/edit', element: <BookingsEdit /> },
        ],
      },

      {
        path: 'bugs',
        children: [
          { index: true, element: <BugsList /> },
          { path: 'new', element: <BugsNew /> },
          { path: ':id', element: <BugsDetails /> },
          { path: ':id/edit', element: <BugsEdit /> },
        ],
      },

      {
        path: 'clubs',
        children: [
          { index: true, element: <ClubsList /> },
          { path: 'new', element: <ClubsNew /> },
          { path: ':id', element: <ClubsDetails /> },
          { path: ':id/edit', element: <ClubsEdit /> },
        ],
      },

      {
        path: 'facilities',
        children: [
          { index: true, element: <FacilitiesList /> },
          { path: 'new', element: <FacilitiesNew /> },
          { path: ':id', element: <FacilitiesDetails /> },
          { path: ':id/edit', element: <FacilitiesEdit /> },
        ],
      },

      {
        path: 'leaderboard',
        children: [
          { index: true, element: <LeaderboardList /> },
          { path: ':id', element: <LeaderboardDetails /> },
        ],
      },

      {
        path: 'matches',
        children: [
          { index: true, element: <MatchesList /> },
          { path: ':id', element: <MatchesDetails /> },
          { path: ':id/edit', element: <MatchesEdit /> },
          { path: ':id/live', element: <MatchesLive /> },
          { path: ':id/players', element: <MatchesPlayers /> },
        ],
      },

      { path: 'monitors', element: <MonitorsList /> },

      {
        path: 'open-play',
        element: openPlayWrapper,
        children: [
          { index: true, element: <OpenPlayHome /> },
          { path: 'board', element: <OpenPlayBoard /> },
          { path: 'queue', element: <OpenPlayQueue /> },
          { path: 'waiting', element: <OpenPlayWaiting /> },
        ],
      },

      {
        path: 'payments',
        children: [
          { index: true, element: <PaymentsList /> },
          // Declared before ':id' so the static segment cannot be read as a payment id.
          { path: 'reports', element: <PaymentsRevenueAnalytics /> },
          { path: ':id', element: <PaymentsDetails /> },
        ],
      },

      {
        path: 'wallet',
        children: [{ index: true, element: <WalletTransactions /> }],
      },

      {
        path: 'vouchers',
        children: [
          { index: true, element: <VoucherCampaignsList /> },
          // Static segments before ':id' so they are not read as campaign ids.
          { path: 'new', element: <VoucherCampaignNew /> },
          { path: 'redeem', element: <VoucherRedeem /> },
          { path: 'redemptions', element: <VoucherRedemptions /> },
          { path: ':id', element: <VoucherCampaignDetails /> },
          { path: ':id/edit', element: <VoucherCampaignEdit /> },
        ],
      },

      {
        path: 'reports',
        children: [
          { path: 'club-performance', element: <ReportsClubPerformance /> },
          { path: 'revenue-analytics', element: <ReportsRevenueAnalytics /> },
          { path: 'system-usage', element: <ReportsSystemUsage /> },
          { path: 'user-activity', element: <ReportsUserActivity /> },
        ],
      },

      {
        path: 'sessions',
        children: [
          { index: true, element: <SessionsList /> },
          { path: 'new', element: <SessionsNew /> },
          { path: ':id', element: <SessionsDetails /> },
          { path: ':id/edit', element: <SessionsEdit /> },
          { path: ':id/court-lineup', element: <SessionsCourtLineup /> },
          {
            path: 'recurring',
            children: [
              { index: true, element: <SessionsRecurringList /> },
              { path: ':id', element: <SessionsRecurringDetails /> },
              { path: ':id/edit', element: <SessionsRecurringEdit /> },
            ],
          },
        ],
      },

      {
        path: 'settings',
        children: [
          { index: true, element: <SettingsHome /> },
          { path: 'health', element: <SettingsHealth /> },
          { path: 'maintenance', element: <SettingsMaintenance /> },
        ],
      },

      {
        path: 'support',
        children: [
          { index: true, element: <SupportList /> },
          { path: 'new', element: <SupportNew /> },
          { path: ':id', element: <SupportDetails /> },
          { path: ':id/edit', element: <SupportEdit /> },
        ],
      },

      {
        path: 'account',
        children: [
          { path: 'profile', element: <AccountProfile /> },
          { path: 'settings', element: <AccountSettings /> },
        ],
      },

      {
        path: 'users',
        children: [
          { index: true, element: <UsersList /> },
          { path: 'list', element: <UsersListAlt /> },
          { path: 'profile', element: <UsersProfile /> },
          // Nested, not a second page: details and edit are one mounted view, so
          // switching between them keeps the loaded user, wallet and tab state.
          {
            path: ':id',
            element: <UsersDetails />,
            children: [{ index: true }, { path: 'edit' }],
          },
        ],
      },
    ],
  },
];
